import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Shield } from "lucide-react";
import { userGroupValidators } from "@shared/validators/admin/user-groups-validators";
import { useCreateUserGroupMutation, useUpdateUserGroupMutation } from "@/api/mutations/admin/UserGroupMutations";
import { Alert, AlertDescription } from "@/components/ui/alert";

type UserGroup = z.infer<typeof userGroupValidators.userGroupSchema>;

interface UserGroupFormProps {
    userGroup?: UserGroup | null;
    mode?: "create" | "edit";
    onSuccess?: () => void;
}

const UserGroupForm = ({ userGroup, mode = "create", onSuccess }: UserGroupFormProps) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const isEditing = mode === "edit" && userGroup;
    const isIdpGroup = userGroup?.externalId != null;

    const formSchema = isEditing
        ? userGroupValidators.updateUserGroupSchema
        : userGroupValidators.createUserGroupSchema;

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: isEditing
            ? {
                  name: userGroup.name,
                  description: userGroup.description,
              }
            : {
                  name: "",
                  description: "",
              },
    });

    const createMutation = useCreateUserGroupMutation(onSuccess, setLoading, setError);
    const updateMutation = useUpdateUserGroupMutation(
        userGroup?.id || "",
        onSuccess,
        setLoading,
        setError
    );

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        if (isEditing) {
            updateMutation.mutate({
                id: userGroup.id,
                data: values,
            });
        } else {
            createMutation.mutate(values as z.infer<typeof userGroupValidators.createUserGroupSchema>);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {isIdpGroup && (
                    <Alert>
                        <Shield className="h-4 w-4" />
                        <AlertDescription>
                            This group is synced from your Identity Provider (IDP). The name and description are managed automatically and cannot be edited manually. User memberships are synced every 10 minutes and on login.
                        </AlertDescription>
                    </Alert>
                )}

                {error && (
                    <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                        {error}
                    </div>
                )}

                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Group Name</FormLabel>
                            <FormControl>
                                <Input 
                                    placeholder="e.g., Engineering Team, Sales Department" 
                                    {...field} 
                                    disabled={loading || isIdpGroup}
                                    className={isIdpGroup ? "opacity-60" : ""}
                                />
                            </FormControl>
                            <FormDescription>
                                {isIdpGroup 
                                    ? "Synced from IDP - cannot be edited manually"
                                    : "A unique name to identify this user group."}
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <Textarea
                                    placeholder="Describe the purpose and members of this group..."
                                    className={`min-h-[100px] ${isIdpGroup ? "opacity-60" : ""}`}
                                    {...field}
                                    disabled={loading || isIdpGroup}
                                />
                            </FormControl>
                            <FormDescription>
                                {isIdpGroup
                                    ? "Synced from IDP - cannot be edited manually"
                                    : "Provide details about who should be in this group and what they can access."}
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onSuccess} disabled={loading}>
                        {isIdpGroup ? "Close" : "Cancel"}
                    </Button>
                    {!isIdpGroup && (
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isEditing ? "Update Group" : "Create Group"}
                        </Button>
                    )}
                </div>
            </form>
        </Form>
    );
};

export default UserGroupForm;
